import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { formatMoney, isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import { sessionStore } from '../platform';
import { useNotifications, useDismissNotification, type NotificationRow } from '../lib/notifications';
import { usePendingCount } from '../lib/approvals';
import {
  Wallet,
  Bell,
  ArrowRight,
  X,
  User,
  Inbox,
  AlertCircle,
  FileCheck,
  CheckCircle2,
} from 'lucide-react';

/**
 * Global Reminder & Notification Popup Modal.
 *
 * Automatically pops up when:
 * 1. User logs in, signs out & logs in again, or opens the app/tab fresh.
 * 2. Active payment dues, pending approvals, or unread notifications exist.
 */
export default function PaymentReminder() {
  const actor = useSession((s) => s.actor);
  const navigate = useNavigate();
  const { data: notifications, isLoading } = useNotifications();
  const { data: pendingApprovals } = usePendingCount();
  const dismissOne = useDismissNotification();
  const [open, setOpen] = useState(false);

  const admin = isAdmin(actor);

  // Filter payment dues (strictly for admin)
  const dueProjects = admin
    ? (notifications ?? []).filter(
        (n) => n.kind === 'payment_due' && (n.amountPaise ?? 0) > 0,
      )
    : [];

  // Other unread alerts / notifications
  const unreadAlerts = (notifications ?? []).filter(
    (n) => n.kind !== 'payment_due' && !n.readAt,
  );

  const hasPendingApprovals = admin && (pendingApprovals ?? 0) > 0;
  const hasDuePayments = admin && dueProjects.length > 0;
  const hasUnreadAlerts = unreadAlerts.length > 0;

  const shouldNotify = hasDuePayments || hasPendingApprovals || hasUnreadAlerts;

  useEffect(() => {
    if (!actor || isLoading) return;

    if (!shouldNotify) {
      setOpen(false);
      return;
    }

    // Check if user dismissed the reminder during this tab/browser session
    if (sessionStore.get('reminder-dismissed-session') === 'true') {
      return;
    }

    setOpen(true);
  }, [actor, isLoading, shouldNotify]);

  function close() {
    sessionStore.set('reminder-dismissed-session', 'true');
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open || !actor || !shouldNotify) return null;

  const totalOutstanding = dueProjects.reduce((s, n) => s + (n.amountPaise ?? 0), 0);

  return createPortal(
    <>
      <div className="scrim" onClick={close} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Pending reminders and notifications"
        className="reminder-popup-modal"
      >
        {/* Header */}
        <div className="reminder-modal-header">
          <div className={`reminder-modal-icon-wrap ${hasDuePayments ? '' : 'alert'}`}>
            {hasDuePayments ? <Wallet size={20} strokeWidth={2.2} /> : <Bell size={20} strokeWidth={2.2} />}
          </div>
          <div className="reminder-modal-title-wrap">
            <h3 className="reminder-modal-title">
              {hasDuePayments
                ? `${dueProjects.length} Project${dueProjects.length === 1 ? '' : 's'} Awaiting Client Payment`
                : hasPendingApprovals
                ? `${pendingApprovals} Site Submissions Awaiting Approval`
                : 'Important Notifications & Site Updates'}
            </h3>
            <div className="reminder-modal-sub">
              {hasDuePayments ? (
                <>
                  <span>Total Outstanding Dues:</span>
                  <strong>{formatMoney(totalOutstanding)}</strong>
                </>
              ) : (
                <span>Action required on your active site entries and approvals</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="reminder-modal-close"
            onClick={close}
            aria-label="Close reminder popup"
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal Content Body */}
        <div className="reminder-modal-body">
          {/* 1. Pending Approvals Banner for Admins */}
          {hasPendingApprovals && (
            <div
              className="reminder-item-card"
              onClick={() => {
                close();
                navigate('/approvals');
              }}
              style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-sm)',
                    background: '#dbeafe',
                    color: 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Inbox size={16} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>
                    {pendingApprovals} Site Entry {pendingApprovals === 1 ? 'Submission' : 'Submissions'} Waiting for Approval
                  </div>
                  <div style={{ fontSize: 11.5, color: '#3b82f6', marginTop: 2 }}>
                    Purchases, labour logs, material usages, and expense entries
                  </div>
                </div>
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--accent)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                Review <ArrowRight size={13} />
              </span>
            </div>
          )}

          {/* 2. Outstanding Client Payment Dues */}
          {dueProjects.length > 0 && (
            <>
              {hasPendingApprovals && (
                <div className="reminder-section-title">Pending Client Payments</div>
              )}
              {dueProjects.map((n) => (
                <div
                  key={n.id}
                  className="reminder-item-card"
                  onClick={() => {
                    close();
                    if (n.projectId) navigate(`/projects/${n.projectId}`);
                  }}
                >
                  <div className="reminder-item-main">
                    <div className="reminder-item-title-row">
                      <span className="reminder-item-name">{n.projectName ?? n.title}</span>
                      {n.clientName && (
                        <span className="reminder-item-client-badge">
                          <User size={11} />
                          {n.clientName}
                        </span>
                      )}
                    </div>
                    <div className="reminder-item-details">
                      {n.projectValuePaise != null && (
                        <span>
                          Contract: <strong>{formatMoney(n.projectValuePaise)}</strong>
                        </span>
                      )}
                      {n.paidAmountPaise != null && (
                        <span>
                          Received: <strong style={{ color: '#059669' }}>{formatMoney(n.paidAmountPaise)}</strong>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="reminder-item-amount">
                    <div className="reminder-item-val">{formatMoney(n.amountPaise)}</div>
                    <div className="reminder-item-lbl">Pending Due</div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* 3. Other unread notifications / alerts */}
          {unreadAlerts.slice(0, 3).map((n) => (
            <div
              key={n.id}
              className="reminder-item-card"
              onClick={() => {
                close();
                navigate('/notifications');
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--surface-2)',
                    color: 'var(--text-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {n.kind === 'entry_rejected' ? (
                    <AlertCircle size={16} style={{ color: '#dc2626' }} />
                  ) : n.kind === 'entry_approved' ? (
                    <CheckCircle2 size={16} style={{ color: '#059669' }} />
                  ) : (
                    <Bell size={16} />
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    {n.title}
                  </div>
                  {n.body && (
                    <div style={{ fontSize: 11.5, color: 'var(--text-soft)', marginTop: 2 }}>
                      {n.body}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="reminder-modal-footer">
          <span className="reminder-modal-note">
            Appears on login & site startup while dues or items remain open
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <button
              type="button"
              className="btn btn-auto btn-sm btn-ghost"
              onClick={close}
            >
              Remind Later
            </button>
            <button
              type="button"
              className="btn btn-auto btn-sm btn-primary"
              onClick={() => {
                close();
                navigate(hasPendingApprovals && !hasDuePayments ? '/approvals' : '/notifications');
              }}
            >
              <span>{hasPendingApprovals && !hasDuePayments ? 'View Approvals' : 'View All'}</span>
              <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
